import theme from "theme";
import { TestCaseStatus } from "generated/graphql";
import { ProfileGenTestCaseViewParsed } from "redux/slices/lensEditor";
import { useState } from "react";
import { stripNull } from "helpers/coll";
import { Checkbox, IconButton, TableCell, TableRow, Tooltip } from "@mui/material";
import { EditableTestCaseDescription } from "./TestCaseLabelsAndDescription";
import LoadingPattern from "components/LoadingPattern";
import { Link } from "react-router-dom";
import { AppRoutes } from "AppRoutes";
import { displayDateAndTimeString } from "Backend";
import { Delete, Edit } from "@mui/icons-material";
import TestCaseResult from "./TestCaseResult";
import EditProfileResponseDialog from "./EditProfileResponseDialog";

type Props = {
  testCase: ProfileGenTestCaseViewParsed;
  lensId: number;
  handleDelete: (id: number) => void;
  selectedIds: number[];
  toggleSelectTestCase: () => void;
  createdBy: string | undefined;
  expanded: boolean;
  toggleExpanded: () => void;
  loading: boolean;
  setPromptId: (id: string | undefined) => void;
};

function TestCaseStatusDisplay({ status }: { status: TestCaseStatus }) {
  switch (status) {
    case TestCaseStatus.Failing:
      return (
        <p className="pattern-link" style={{ color: theme.palette.error.main }}>
          Failing
        </p>
      );
    case TestCaseStatus.Passing:
      return (
        <p className="pattern-link" style={{ color: theme.palette.success.main }}>
          Passing
        </p>
      );
    case TestCaseStatus.Untested:
      return <p className="font-semibold text-gray-900">Untested</p>;
    case TestCaseStatus.Unknown:
      return <p className="font-semibold text-gray-900">Unknown</p>;
  }
}

const linkClasses = "pattern-link whitespace-nowrap";

export function TestCase({
  testCase,
  handleDelete,
  selectedIds,
  toggleSelectTestCase,
  createdBy,
  expanded,
  toggleExpanded,
  loading,
  setPromptId,
}: Props) {
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);

  const lastResult = stripNull(testCase.lastResult);
  const testChat = lastResult && lastResult.chatId;

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Checkbox checked={selectedIds.includes(testCase.id)} onChange={toggleSelectTestCase} />
        </TableCell>
        <TableCell className="truncate">
          <EditableTestCaseDescription testCase={testCase} />
        </TableCell>
        <TableCell onClick={toggleExpanded} align="center">
          {loading ? <LoadingPattern centered size={16} /> : <TestCaseStatusDisplay status={testCase.status} />}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <p className={linkClasses}>
              <Link to={AppRoutes.aiChatBuilder(testCase.lensId, testCase.sourceAiChatId)} target="_blank">
                Source Chat
              </Link>
            </p>
            {"\u2022"}
            <p className={linkClasses} onClick={() => setPromptId(testCase.sourceAiChatId)}>
              Source Blocks
            </p>
            {testChat && (
              <>
                {"\u2022"}
                <p className={linkClasses} onClick={() => setPromptId(testChat)}>
                  Test Blocks
                </p>
                {"\u2022"}
                <p className={linkClasses}>
                  <Link to={AppRoutes.aiChatBuilder(testCase.lensId, testChat)} target="_blank">
                    Test Chat
                  </Link>
                </p>
              </>
            )}
          </div>
        </TableCell>
        <TableCell>{displayDateAndTimeString(testCase.createdAt)}</TableCell>
        <TableCell>{createdBy}</TableCell>
        <TableCell align="right">
          <Tooltip title="Edit Expectation">
            <IconButton
              onClick={() => {
                setShowEditDialog(true);
              }}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Test Case">
            <IconButton onClick={() => handleDelete(testCase.id)}>
              <Delete />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      {expanded && lastResult && (
        <TableRow>
          <TableCell />
          <TableCell colSpan={4}>
            <TestCaseResult testCase={testCase} />
          </TableCell>
        </TableRow>
      )}
      {showEditDialog && (
        <EditProfileResponseDialog testCase={testCase} open={true} onClose={() => setShowEditDialog(false)} />
      )}
    </>
  );
}

export default TestCase;
